import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUserRbacPermissions } from 'core/userContext';
import { BUTTON_SIZES } from 'ui/button';
import { LinkAddButton } from 'ui/linkButton';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { PROJECT_PERMISSIONS } from 'utils/permissions';

import { IndexHeaderActionContainer, WideInputText } from './Styled';
import { snippetUrlNew, SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS } from '../constants';
import { useSnippetFiltersContext } from '../context/SnippetFiltersContext';
import { useTranslations } from '../hooks/useTranslations';

export const SnippetIndexHeaderActions: FC = () => {
  const { t } = useTranslations();
  const { searchQuery, setSearchQuery } = useSnippetFiltersContext();
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);

  const permissions = useSelector(selectUserRbacPermissions);
  const canManageSnippets = useMemo(
    () => permissions.includes(PROJECT_PERMISSIONS.ManageSnippets),
    [permissions],
  );

  // On debounced changes to `searchInputValue`, save the search to local storage and trigger a GraphQL refetch
  useDebouncedEffect(() => setSearchQuery(searchInputValue), [searchInputValue], {
    skipEffectOnMount: true,
    debounceTimeout: SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS,
  });

  return (
    <IndexHeaderActionContainer>
      <WideInputText
        id={'snippet-search'}
        onChange={setSearchInputValue}
        placeholder={t('input.search.placeholder')}
        value={searchInputValue}
      />
      {canManageSnippets && (
        <LinkAddButton
          to={snippetUrlNew}
          size={BUTTON_SIZES.LARGE}
          isDisabled={false}
          data-test={'create-new-snippet-button'}
        >
          {t('button.newSnippet')}
        </LinkAddButton>
      )}
    </IndexHeaderActionContainer>
  );
};

export default SnippetIndexHeaderActions;
